import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@shared/database.types";

type Task = Database["public"]["Tables"]["tasks"]["Row"];
type TaskInsert = Database["public"]["Tables"]["tasks"]["Insert"];
type TaskUpdate = Database["public"]["Tables"]["tasks"]["Update"];

export interface TaskFilters {
  clientId?: string | null;
  assignedTo?: string | null;
  type?: "client_visible" | "internal" | null;
}

export function useTasks(filters: TaskFilters = {}) {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["tasks", agencyId, filters],
    enabled: !!agencyId,
    queryFn: async (): Promise<Task[]> => {
      let q = supabase
        .from("tasks")
        .select("*")
        .eq("agency_id", agencyId!)
        .order("position", { ascending: true })
        .order("created_at", { ascending: false });

      if (filters.clientId) q = q.eq("client_id", filters.clientId);
      if (filters.assignedTo) q = q.eq("assigned_to", filters.assignedTo);
      if (filters.type) q = q.eq("type", filters.type);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Task[];
    },
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  const { agencyId, user } = useAuth();

  return useMutation({
    mutationFn: async (input: Omit<TaskInsert, "agency_id" | "created_by">) => {
      if (!agencyId) throw new Error("agency_id mancante");
      const { data, error } = await supabase
        .from("tasks")
        .insert({ ...input, agency_id: agencyId, created_by: user?.id ?? null })
        .select()
        .single();
      if (error) throw error;
      return data as Task;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: TaskUpdate }) => {
      const { data, error } = await supabase
        .from("tasks")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as Task;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export interface TaskPositionUpdate {
  id: string;
  position: number;
  /** Key di task_statuses (la pipeline è personalizzabile, niente enum) */
  status?: string;
}

/**
 * Aggiorna in batch position (ed eventuale status) di più task.
 * Usato dal kanban per l'ordinamento persistente: punto medio tra i vicini
 * al drop, o rinumerazione dell'intera colonna a step di 1000 in caso di
 * collisione.
 */
export function useUpdateTaskPositions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: TaskPositionUpdate[]) => {
      const results = await Promise.all(
        updates.map(({ id, position, status }) =>
          supabase
            .from("tasks")
            .update(status ? { position, status } : { position })
            .eq("id", id),
        ),
      );
      for (const r of results) {
        if (r.error) throw r.error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

/**
 * Applica la stessa patch a più task in un colpo solo (barra di selezione).
 * Una sola query `in (...)`: con 40 task selezionate è una richiesta, non 40.
 */
export function useBulkUpdateTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, patch }: { ids: string[]; patch: TaskUpdate }) => {
      if (ids.length === 0) return 0;
      const { error } = await supabase.from("tasks").update(patch).in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useBulkDeleteTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return 0;
      const { error } = await supabase.from("tasks").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tasks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

// Hook che attiva il subscribe Realtime alle modifiche tasks della propria agenzia
// e invalida la cache TanStack Query. Sostituisce il vecchio refetchInterval.
export function useRealtimeTasks() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();

  useEffect(() => {
    if (!agencyId) return;

    // Channel name unico per istanza: evita "cannot add callbacks after subscribe()"
    // quando StrictMode o un remount riutilizza lo stesso nome canale già sottoscritto.
    const channelName = `tasks:${agencyId}:${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          filter: `agency_id=eq.${agencyId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["tasks"] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [agencyId, qc]);
}
